/**
 * Aggregated overview shape — one slice per dashboard card.
 *
 * Consumed by every card under `components/`. The pure
 * `aggregateOverview()` produces this object from the raw backend
 * responses; cards never see the raw shape, so renaming a backend
 * field touches only one file (`aggregateOverview.ts`).
 *
 * Fields that depend on agents the user hasn't reached yet are
 * either `null` or zero — cards must render a graceful empty state,
 * never crash on missing data.
 */

import type { StepStatus } from '../../athena/state/workflowV2Types';

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type Treatment = 'reduce' | 'share' | 'retain' | 'avoid';
export type CalLevel = 'CAL1' | 'CAL2' | 'CAL3' | 'CAL4';

export interface StatusBannerSlice {
  phase: 'not_started' | 'in_progress' | 'completed' | 'failed';
  currentStep: number;          // 1..8 (Step 0 collapses into 1 for the banner)
  stepStatuses: StepStatus[];   // length 8, one per step 1..8
  lastActivity: string | null;  // ISO-8601 timestamp or null
  analysisMode: 'individual' | 'hierarchical' | null;
  importedChildTaras: number;   // 0 for individual mode
  counts: {
    assets: number;
    damageScenarios: number;
    threats: number;
    attackPaths: number;
    risks: number;
    goals: number;
    claims: number;
    controls: number;
    requirements: number;
    vvRecommendations: number;
  };
}

export interface TreatmentMixSlice {
  reduce: number;
  share: number;
  retain: number;
  avoid: number;
}

export interface UntreatedRisksSlice {
  count: number;     // risks at Critical/High with no treatment
  critical: number;
  high: number;
}

export type RiskDistributionSlice = Record<Severity, number>;

export type TreatmentKey = keyof TreatmentMixSlice;

/** 4×4 joint distribution: severity (rows) × treatment decision
 *  (columns). Replaces the separate Risk Distribution + Treatment
 *  Decisions cards with one heat map. */
export interface RiskTreatmentMatrixSlice {
  /** cells[severity][treatment] = risk count */
  cells: Record<Severity, Record<TreatmentKey, number>>;
  /** risks counted into the matrix (have both a severity and a treatment) */
  total: number;
  /** highest single-cell count — drives the sequential color scale */
  max: number;
  /** risks with a severity but no recognizable treatment decision */
  untreated: number;
}

export interface Iso21434ClauseRow {
  ref: string;         // e.g. "9.3"
  name: string;        // e.g. "Item Definition"
  status: StepStatus;  // empty | generating | draft | revising | approved | failed
}

export interface Unr155SectionRow {
  name: string;
  pct: number;        // 0..100
  classified: number;
  total: number;
}

export interface Unr155Slice {
  overallPct: number;
  sections: Unr155SectionRow[];
}

export interface CoverageEdge {
  from: string;
  to: string;
  pct: number;        // 0..100
}

export type StrideKey =
  | 'spoofing'
  | 'tampering'
  | 'repudiation'
  | 'information_disclosure'
  | 'denial_of_service'
  | 'elevation_of_privilege';

export type StrideSlice = Record<StrideKey, number>;

export interface AttackPathRow {
  id: string;
  asset: string;          // joined asset name (best-effort)
  risk: Severity;
  treatment: Treatment | null;
  feasibility: 'High' | 'Medium' | 'Low' | 'Very Low' | null;
}

export interface OrphanFinding {
  id: string;             // synthetic key
  type:
    | 'asset'
    | 'scenario'
    | 'threat'
    | 'attack_path'
    | 'risk'
    | 'goal'
    | 'requirement';
  target: string;         // record identifier from its step
  desc: string;
  step: number;
}

export interface ChildTaraSummary {
  name: string;
  records: number;
  relevant: number;
  status: StepStatus;
}

export interface CalEffortRow {
  level: CalLevel;
  count: number;
  pct: number;            // 0..100, share of total controls/requirements
  desc: string;           // canonical CAL description
}

export interface AggregatedOverviewData {
  statusBanner: StatusBannerSlice;
  treatmentMix: TreatmentMixSlice;
  untreatedRisks: UntreatedRisksSlice;
  riskScore: number;                       // 0..10 overall
  riskDistribution: RiskDistributionSlice;
  riskTreatmentMatrix: RiskTreatmentMatrixSlice;
  iso21434: Iso21434ClauseRow[];           // 8 rows
  unr155: Unr155Slice | null;              // null until threats approved
  coverage: CoverageEdge[];                // 7 edges (Assets→Scenarios … Reqs→V&V)
  stride: StrideSlice;
  topAttackPaths: AttackPathRow[];         // top 5 by risk severity
  orphans: OrphanFinding[];                // uncapped — one per broken link, all 7 edges
  inheritance: ChildTaraSummary[] | null;  // null in individual mode
  calEffort: CalEffortRow[];               // 4 rows (CAL1..CAL4)
}

/** Empty aggregate — every card's empty state. Returned when no thread exists. */
export const EMPTY_AGGREGATE: AggregatedOverviewData = {
  statusBanner: {
    phase: 'not_started',
    currentStep: 1,
    stepStatuses: ['empty', 'empty', 'empty', 'empty', 'empty', 'empty', 'empty', 'empty'],
    lastActivity: null,
    analysisMode: null,
    importedChildTaras: 0,
    counts: {
      assets: 0,
      damageScenarios: 0,
      threats: 0,
      attackPaths: 0,
      risks: 0,
      goals: 0,
      claims: 0,
      controls: 0,
      requirements: 0,
      vvRecommendations: 0,
    },
  },
  treatmentMix: { reduce: 0, share: 0, retain: 0, avoid: 0 },
  untreatedRisks: { count: 0, critical: 0, high: 0 },
  riskScore: 0,
  riskDistribution: { critical: 0, high: 0, medium: 0, low: 0 },
  riskTreatmentMatrix: {
    cells: {
      critical: { reduce: 0, share: 0, retain: 0, avoid: 0 },
      high: { reduce: 0, share: 0, retain: 0, avoid: 0 },
      medium: { reduce: 0, share: 0, retain: 0, avoid: 0 },
      low: { reduce: 0, share: 0, retain: 0, avoid: 0 },
    },
    total: 0,
    max: 0,
    untreated: 0,
  },
  iso21434: [],
  unr155: null,
  coverage: [],
  stride: {
    spoofing: 0,
    tampering: 0,
    repudiation: 0,
    information_disclosure: 0,
    denial_of_service: 0,
    elevation_of_privilege: 0,
  },
  topAttackPaths: [],
  orphans: [],
  inheritance: null,
  calEffort: [
    { level: 'CAL1', count: 0, pct: 0, desc: 'Same team + basic testing' },
    { level: 'CAL2', count: 0, pct: 0, desc: 'Different person + structured testing' },
    { level: 'CAL3', count: 0, pct: 0, desc: 'Different team + semi-formal verification' },
    { level: 'CAL4', count: 0, pct: 0, desc: 'Third-party audit + formal verification' },
  ],
};
